/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';

import { Column } from 'primereact/column';

import {
  DataTable,
  DataTableFilterMeta,
  DataTableSelectEvent,
  DataTableSelectionSingleChangeEvent
} from 'primereact/datatable';

import { ProcessSummary } from '../types/processing-schemas.ts';
import { FilterMatchMode } from 'primereact/api';

import { defaultProcessDatableColumns } from './ProcessesDataTable/ProcessColumnRender.tsx';
import { DataTableColumns, Severity, TableHeader, TableHeaderProps } from './TableHeader.tsx';
import { FilterProps } from '../types/models-schemas.ts';
import { ActionBodyRender, ActionsProps } from './ActionBodyRender.tsx';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

export interface ProcessesDataTableProps extends FilterProps {
  processes: ProcessSummary[];
  className?: string;
  dataTableClassName?: string;
  columns?: DataTableColumns[];
  loading?: boolean;
  selection?: ProcessSummary;
  onSelectionChange?: (ev: DataTableSelectionSingleChangeEvent<ProcessSummary[]>) => void;
  onRowUnselected?: (ev: DataTableSelectEvent) => void;
  actions?: Array<ActionsProps<ProcessSummary>>;
  getCapabilities: TableHeaderProps['getCapabilities'];
  onRefresh?: {
    severity?: Severity;
    command?: () => Promise<unknown>;
  };
}

const defaultFilters = (filterProperties?: Record<string, unknown>): DataTableFilterMeta => {
  return {
    global: { value: filterProperties?.global ?? null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: filterProperties?.id ?? null, matchMode: FilterMatchMode.STARTS_WITH },
    description: { value: filterProperties?.description ?? null, matchMode: FilterMatchMode.CONTAINS },
    keywords: { value: filterProperties?.keywords ?? null, matchMode: FilterMatchMode.IN }
  };
};

export const ProcessesDataTable: React.FC<ProcessesDataTableProps> = (props) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters());

  const [globalFilterValue, setGlobalFilterValue] = useState<string>(null);

  const getMapColumns = () => {
    return props.columns ?? defaultProcessDatableColumns;
  };

  const initFilters = (_defaultFilters?: Record<string, unknown>) => {
    const _filters = defaultFilters(_defaultFilters);
    setFilters(_filters);
    setGlobalFilterValue(_filters.global['value']);
  };

  useEffect(() => {
    initFilters(props.defaultFilters);
  }, [props.defaultFilters]);

  const clearFilters = (): void => {
    initFilters();
    if (props.onClearFilters) {
      props.onClearFilters.command?.(defaultFilters());
    }
  };

  return (
    <div className={classNames('w-full mt-2', props.className)}>
      <DataTable
        header={
          <TableHeader
            onRefresh={props.onRefresh}
            filters={filters}
            setFilters={(filters) => {
              setFilters(filters);
              props.handleGlobalFilters?.(filters);
            }}
            globalFilterValue={globalFilterValue}
            setGlobalFilterValue={setGlobalFilterValue}
            onClearFilters={{ severity: props.onClearFilters?.severity, command: clearFilters }}
            getCapabilities={props.getCapabilities}
          />
        }
        dataKey="id"
        filters={filters}
        filterDisplay="row"
        globalFilterFields={['id', 'title', 'message', 'keywords'] as Array<keyof ProcessSummary>}
        paginator
        emptyMessage={t(['misc.404', 'No available data'])}
        rows={5}
        className={props.dataTableClassName}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onSelectionChange={props.onSelectionChange}
        onRowSelect={props.onRowUnselected}
        selection={props.selection}
        selectionMode={'single'}
        stripedRows
        loading={props.loading}
        size={'small'}
        value={props.processes}
        tableStyle={{ width: '100%' }}
      >
        {getMapColumns()
          .toSorted((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY))
          .map((c, index) => (
            <Column columnKey={c.field} className="cursor-pointer" key={index} {...c} />
          ))}
        {props.actions && props.actions.length > 0 && (
          <Column
            header="Actions"
            align="center"
            body={(item) => <ActionBodyRender<ProcessSummary> item={item} actions={props.actions} />}
          />
        )}
      </DataTable>
    </div>
  );
};
